/**
 * Preferences API - Reads/writes user preferences from Firestore
 *
 * Document path: superjarvis_users/{uid}/preferences/settings
 *
 * Provides:
 * - CRUD operations for user preferences
 * - Real-time subscription via onSnapshot
 * - React hook with optimistic UI updates
 * - Password reset via Firebase Auth
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Timestamp,
  DocumentSnapshot,
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from './firebase';
import { useAuthContext } from '../contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

export interface UserPreferences {
  // Privacy
  dataSharing: boolean;
  analyticsOptIn: boolean;

  // Security
  twoFactorEnabled: boolean;

  // Language & Display
  language: string;
  responseLanguage: string;
  theme: 'dark' | 'light' | 'system';

  // Subscription (read-only from client - billing not implemented)
  plan: 'free' | 'pro' | 'super_pro' | 'enterprise';

  // Metadata
  updatedAt: Timestamp;
}

/** Keys that users can update (excludes server-managed fields) */
export type UpdatablePreferenceKey = keyof Omit<UserPreferences, 'updatedAt' | 'plan'>;

const DEFAULT_PREFERENCES: Omit<UserPreferences, 'updatedAt'> = {
  dataSharing: false,
  analyticsOptIn: true,
  twoFactorEnabled: false,
  language: 'en-US',
  responseLanguage: 'en-US',
  theme: 'dark',
  plan: 'free',
};

// ============================================================================
// Firestore CRUD
// ============================================================================

function prefsDocRef(uid: string) {
  return doc(db, 'superjarvis_users', uid, 'preferences', 'settings');
}

/**
 * Get user preferences from Firestore (one-time read).
 * Creates the document with defaults if it doesn't exist.
 */
export async function getPreferences(uid: string): Promise<UserPreferences> {
  const ref = prefsDocRef(uid);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    return {
      ...DEFAULT_PREFERENCES,
      ...snap.data(),
      updatedAt: snap.data().updatedAt ?? Timestamp.now(),
    } as UserPreferences;
  }

  // Bootstrap defaults
  const defaults: UserPreferences = {
    ...DEFAULT_PREFERENCES,
    updatedAt: Timestamp.now(),
  };
  await setDoc(ref, defaults);
  return defaults;
}

/**
 * Update user preferences in Firestore (merge).
 */
export async function updatePreferences(
  uid: string,
  updates: Partial<Omit<UserPreferences, 'updatedAt'>>,
): Promise<void> {
  await setDoc(prefsDocRef(uid), { ...updates, updatedAt: Timestamp.now() }, { merge: true });
}

/**
 * Subscribe to real-time preference changes.
 * Returns an unsubscribe function.
 */
export function subscribeToPreferences(
  uid: string,
  callback: (prefs: UserPreferences) => void,
): () => void {
  return onSnapshot(prefsDocRef(uid), (snap: DocumentSnapshot) => {
    if (snap.exists()) {
      callback({
        ...DEFAULT_PREFERENCES,
        ...snap.data(),
        updatedAt: snap.data().updatedAt ?? Timestamp.now(),
      } as UserPreferences);
    } else {
      callback({ ...DEFAULT_PREFERENCES, updatedAt: Timestamp.now() });
    }
  });
}

// ============================================================================
// Auth Actions
// ============================================================================

/**
 * Send a password reset/set email to the currently signed-in user.
 * Works for both "change password" and "set initial password" flows.
 */
export async function triggerPasswordReset(): Promise<void> {
  const user = auth.currentUser;
  if (!user?.email) {
    throw new Error('No email associated with this account');
  }
  await sendPasswordResetEmail(auth, user.email);
}

/**
 * Determine the authentication provider for the current user.
 */
export function getAuthProvider(): 'google' | 'email' | 'unknown' {
  const user = auth.currentUser;
  if (!user) return 'unknown';
  const providerId = user.providerData[0]?.providerId;
  if (providerId === 'google.com') return 'google';
  if (providerId === 'password' || providerId === 'emailLink') return 'email';
  return 'unknown';
}

// ============================================================================
// React Hook
// ============================================================================

export interface UsePreferencesReturn {
  /** Current preferences (null while loading) */
  preferences: UserPreferences | null;
  /** True during initial load */
  loading: boolean;
  /** Error message if something went wrong */
  error: string | null;
  /** Update a single preference key - optimistic with rollback */
  updatePref: <K extends UpdatablePreferenceKey>(
    key: K,
    value: UserPreferences[K],
  ) => Promise<void>;
  /** Send a password reset email to the current user */
  resetPassword: () => Promise<void>;
  /** The current user's auth provider */
  authProvider: 'google' | 'email' | 'unknown';
}

export function usePreferences(): UsePreferencesReturn {
  const { user } = useAuthContext();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep a stable ref to the latest prefs for rollback
  const prefsRef = useRef<UserPreferences | null>(null);
  prefsRef.current = preferences;

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.uid) {
      setPreferences(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    // Ensure document exists, then attach listener
    getPreferences(user.uid)
      .then(() => {
        if (cancelled) return;
        unsubscribe = subscribeToPreferences(user.uid, (prefs) => {
          if (!cancelled) {
            setPreferences(prefs);
            setLoading(false);
          }
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[Preferences] Error loading:', err);
          setError('Failed to load preferences');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [user?.uid]);

  // Optimistic update with rollback on failure
  const updatePref = useCallback(
    async <K extends UpdatablePreferenceKey>(key: K, value: UserPreferences[K]) => {
      if (!user?.uid || !prefsRef.current) return;

      const previous = prefsRef.current[key];

      // Optimistic update
      setPreferences((prev) =>
        prev ? { ...prev, [key]: value, updatedAt: Timestamp.now() } : prev,
      );

      try {
        await updatePreferences(user.uid, { [key]: value });
      } catch (err) {
        console.error('[Preferences] Update failed, rolling back:', err);
        setError('Failed to save preference');
        // Rollback
        setPreferences((prev) => (prev ? { ...prev, [key]: previous } : prev));
      }
    },
    [user?.uid],
  );

  const resetPassword = useCallback(async () => {
    await triggerPasswordReset();
  }, []);

  return {
    preferences,
    loading,
    error,
    updatePref,
    resetPassword,
    authProvider: getAuthProvider(),
  };
}
